import { doc, updateDoc, getDoc, FieldValue } from 'firebase/firestore';
import { db } from '../firebase-config';

class PINHashingService {
  constructor() {
    // Ensure we're in a browser environment for crypto
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
      throw new Error('Web Crypto API is not available');
    }
  }

  /**
   * Generates a random 32-byte salt
   * @returns {string} Base64 encoded salt
   */
  generateSalt() {
    const saltArray = new Uint8Array(32);
    window.crypto.getRandomValues(saltArray);
    return btoa(String.fromCharCode(...saltArray));
  }

  /**
   * Creates a secure hash for a PIN with salt (matching iOS implementation)
   * @param {string} pin - The 4-digit PIN
   * @returns {Promise<{hash: string, salt: string}>}
   */
  async hashPIN(pin) {
    const salt = this.generateSalt();
    const hash = await this.hashPINWithSalt(pin, salt);
    return { hash, salt };
  }

  /**
   * Creates hash from PIN and salt
   * @param {string} pin - The 4-digit PIN
   * @param {string} salt - Base64 encoded salt
   * @returns {Promise<string>} Base64 encoded hash
   */
  async hashPINWithSalt(pin, salt) {
    try {
      // Decode salt from base64
      const saltData = Uint8Array.from(atob(salt), c => c.charCodeAt(0));
      
      // Convert PIN to bytes
      const pinData = new TextEncoder().encode(pin);
      
      // Combine PIN + salt
      const combinedData = new Uint8Array(pinData.length + saltData.length);
      combinedData.set(pinData);
      combinedData.set(saltData, pinData.length);
      
      // Hash with SHA-256 (matching iOS)
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', combinedData);
      
      // Convert to base64
      const hashArray = new Uint8Array(hashBuffer);
      return btoa(String.fromCharCode(...hashArray));
    } catch (error) {
      console.error('Error hashing PIN:', error);
      throw new Error('Failed to hash PIN');
    }
  }

  /**
   * Verifies a PIN against stored hash and salt
   * @param {string} pin - The entered PIN
   * @param {string} storedHash - The stored hash
   * @param {string} salt - The stored salt
   * @returns {Promise<boolean>}
   */
  async verifyPIN(pin, storedHash, salt) {
    try {
      const computedHash = await this.hashPINWithSalt(pin, salt);
      return computedHash === storedHash;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    }
  }

  /**
   * Validates PIN format (4 digits)
   * @param {string} pin - The PIN to validate
   * @returns {boolean}
   */
  isValidPIN(pin) {
    return /^\d{4}$/.test(pin);
  }

  /**
   * Updates staff PIN in Firebase with secure hash
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string} newPIN - New 4-digit PIN
   * @returns {Promise<void>}
   */
  async updateStaffPIN(companyId, staffId, newPIN) {
    if (!this.isValidPIN(newPIN)) {
      throw new Error('PIN must be exactly 4 digits');
    }

    try {
      const { hash, salt } = await this.hashPIN(newPIN);
      
      const updateData = {
        pinHash: hash,
        pinSalt: salt,
        pinVersion: 'v2',
        pinCreatedAt: new Date(),
        // Remove old plain text PIN
        pin: FieldValue.delete()
      };

      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      await updateDoc(staffRef, updateData);
      
      return updateData;
    } catch (error) {
      console.error('Error updating staff PIN:', error);
      throw error;
    }
  }

  /**
   * Verifies PIN for staff member
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string} enteredPIN - The PIN to verify
   * @returns {Promise<{isValid: boolean, needsMigration: boolean}>}
   */
  async verifyStaffPIN(companyId, staffId, enteredPIN) {
    try {
      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      const docSnap = await getDoc(staffRef);
      
      if (!docSnap.exists()) {
        throw new Error('Staff member not found');
      }

      const data = docSnap.data();
      
      // Check if we have new secure format
      if (data.pinHash && data.pinSalt) {
        const isValid = await this.verifyPIN(enteredPIN, data.pinHash, data.pinSalt);
        return { isValid, needsMigration: false };
      }
      
      // Legacy support: Check old plain text PIN
      if (data.pin) {
        const isValid = (enteredPIN === data.pin);
        
        // If valid, mark for migration
        if (isValid) {
          return { isValid: true, needsMigration: true };
        }
        
        return { isValid: false, needsMigration: false };
      }
      
      throw new Error('No PIN found for staff member');
    } catch (error) {
      console.error('Error verifying staff PIN:', error);
      throw error;
    }
  }

  /**
   * Migrates legacy PIN to secure format
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string} plainTextPIN - The plain text PIN to migrate
   * @returns {Promise<void>}
   */
  async migrateLegacyPIN(companyId, staffId, plainTextPIN) {
    try {
      await this.updateStaffPIN(companyId, staffId, plainTextPIN);
      console.log(`✅ PIN migrated to secure format for staff: ${staffId}`);
    } catch (error) {
      console.error(`⚠️ PIN migration failed for staff ${staffId}:`, error);
      throw error;
    }
  }
}

export default new PINHashingService();